import { useState, useMemo } from 'react';
import { AppCard } from '@/components/ui/AppCard';
import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import { endpoints } from '@/services/endpoints';
import {
  axiosGetRequest,
  axiosPostRequest,
  axiosPutRequest,
  axiosDeleteRequest,
} from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, X, Layers, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import AppPagination from '@/components/ui/AppPagination';
import { Skeleton } from '@/components/ui/Skeleton';
import { TableFilterBar } from '@/components/ui/TableFilterBar';
import { Search } from 'lucide-react';

interface ISubcategory {
  _id: string;
  subCategory: string;
  subCategory_ar?: string;
  category?: string | { _id: string; name_en: string };
}

interface ICategory {
  _id: string;
  name_en: string;
}

export default function Subcategories() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<ISubcategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ISubcategory | null>(null);
  const [form, setForm] = useState({ subCategory: '', subCategory_ar: '', category: '' });
  const [localSearch, setLocalSearch] = useState('');
  const [sortValues, setSortValues] = useState<Record<string, string>>({ sort: '', category: '' });

  const { data: categoriesData } = useQuery({
    queryKey: [endpoints.getAllCategories],
    queryFn: () => axiosGetRequest(endpoints.getAllCategories, { limit: 100, booksLimit: 0 }),
    select: d => d?.result as ICategory[],
  });

  const { data, isLoading } = useQuery({
    queryKey: [endpoints.getAllSubCategory, page, sortValues.category],
    queryFn: () =>
      axiosGetRequest(endpoints.getAllSubCategory, {
        page,
        limit: 15,
        ...(sortValues.category ? { category: sortValues.category } : {}),
      }),
  });

  const addSub = useMutation({
    mutationFn: (body: any) => axiosPostRequest(endpoints.addSubcategory, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllSubCategory] });
      toast.success('Subcategory added');
      closeModal();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to add subcategory'),
  });

  const updateSub = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      axiosPutRequest(`${endpoints.updateSubcategory}?id=${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllSubCategory] });
      toast.success('Subcategory updated');
      closeModal();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to update subcategory'),
  });

  const deleteSub = useMutation({
    mutationFn: (id: string) => axiosDeleteRequest(endpoints.deleteSubcategory, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllSubCategory] });
      toast.success('Subcategory deleted');
      setDeleteTarget(null);
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to delete subcategory'),
  });

  function openAdd() {
    setForm({ subCategory: '', subCategory_ar: '', category: categoriesData?.[0]?._id || '' });
    setEditTarget(null);
    setShowModal(true);
  }

  function openEdit(sub: ISubcategory) {
    const catId = typeof sub.category === 'object' ? sub.category._id : (sub.category || '');
    setForm({ subCategory: sub.subCategory, subCategory_ar: sub.subCategory_ar || '', category: catId });
    setEditTarget(sub);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditTarget(null);
    setForm({ subCategory: '', subCategory_ar: '', category: '' });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.subCategory.trim() || !form.category) {
      toast.error('Subcategory name and category are required');
      return;
    }
    const body: any = { subCategory: form.subCategory, category: form.category };
    if (form.subCategory_ar) body.subCategory_ar = form.subCategory_ar;
    if (editTarget) {
      updateSub.mutate({ id: editTarget._id, body });
    } else {
      addSub.mutate(body);
    }
  }

  const rawSubcategories: ISubcategory[] = data?.result || [];

  // Client-side search + sort
  const subcategories = useMemo(() => {
    let items = rawSubcategories;
    if (localSearch.trim()) {
      const q = localSearch.toLowerCase();
      items = items.filter(s => s.subCategory.toLowerCase().includes(q) || (s.subCategory_ar ?? '').includes(q));
    }
    const sort = sortValues.sort;
    if (sort === 'name_asc') items = [...items].sort((a, b) => a.subCategory.localeCompare(b.subCategory));
    else if (sort === 'name_desc') items = [...items].sort((a, b) => b.subCategory.localeCompare(a.subCategory));
    return items;
  }, [rawSubcategories, localSearch, sortValues.sort]);

  const isPending = addSub.isPending || updateSub.isPending;

  return (
    <div className="space-y-4">
      <AppCard>
        <div className="flex items-center justify-between border-b border-coal-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold text-coal-900">Subcategories</h1>
            {data?.pagination?.totalItems != null && (
              <span className="rounded-full bg-coal-100 px-2 py-0.5 text-xs font-medium text-coal-500">
                {data.pagination.totalItems}
              </span>
            )}
          </div>
          <AppButton size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4" />
            Add Subcategory
          </AppButton>
        </div>

        {/* Search + filter bar */}
        <div className="flex flex-col gap-2 border-b border-coal-100 px-5 py-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-center gap-2 rounded-lg border border-coal-200 bg-white px-2.5 h-8 sm:w-56">
            <Search className="h-3.5 w-3.5 shrink-0 text-coal-400" />
            <input
              type="text"
              placeholder="Search subcategories..."
              className="flex-1 text-xs text-coal-700 placeholder:text-coal-400 focus:outline-none"
              value={localSearch}
              onChange={e => setLocalSearch(e.target.value)}
            />
          </div>
          <TableFilterBar
            filters={[
              {
                key: 'category',
                label: 'Category',
                options: [
                  { value: '', label: 'All Categories' },
                  ...(categoriesData?.map(c => ({ value: c._id, label: c.name_en })) ?? []),
                ],
              },
              {
                key: 'sort',
                label: 'Sort By',
                options: [
                  { value: '', label: 'Default' },
                  { value: 'name_asc', label: 'Name A–Z' },
                  { value: 'name_desc', label: 'Name Z–A' },
                ],
              },
            ]}
            values={sortValues}
            onChange={(k, v) => { setSortValues(prev => ({ ...prev, [k]: v })); setPage(1); }}
            onClearAll={() => { setSortValues({ sort: '', category: '' }); setPage(1); }}
          />
        </div>

        <div className="divide-y divide-coal-100">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3">
                  <Skeleton className="h-4 w-40 rounded flex-1" />
                  <Skeleton className="h-4 w-24 rounded" />
                  <Skeleton className="h-8 w-20 rounded" />
                </div>
              ))
            : subcategories.length === 0
              ? (
                <p className="py-10 text-center text-sm text-coal-400">No subcategories found</p>
              )
              : subcategories.map(sub => {
                const catName = typeof sub.category === 'object'
                  ? sub.category.name_en
                  : categoriesData?.find(c => c._id === sub.category)?.name_en || '—';
                return (
                  <div key={sub._id} className="flex items-center gap-4 px-5 py-3 hover:bg-coal-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-coal-800">{sub.subCategory}</p>
                      {sub.subCategory_ar && (
                        <p className="text-xs text-coal-400 font-arabic">{sub.subCategory_ar}</p>
                      )}
                    </div>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {catName}
                    </span>
                    <div className="flex gap-1.5">
                      <AppButton size="xs" isIconOnly onClick={() => openEdit(sub)}>
                        <Pencil className="h-3.5 w-3.5 text-warning" />
                      </AppButton>
                      <AppButton size="xs" isIconOnly onClick={() => setDeleteTarget(sub)}>
                        <Trash2 className="h-3.5 w-3.5 text-danger" />
                      </AppButton>
                    </div>
                  </div>
                );
              })}
        </div>

        {data?.pagination && (
          <div className="flex justify-center border-t border-coal-200 py-3">
            <AppPagination
              isLoading={isLoading}
              currentPage={data.pagination.currentPage}
              totalPages={data.pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </AppCard>

      {/* Add / Edit Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={closeModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-xl border border-coal-200 bg-white shadow-dropdown">
              <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
                <h3 className="text-base font-semibold text-coal-900">
                  {editTarget ? 'Edit Subcategory' : 'Add Subcategory'}
                </h3>
                <button className="rounded-md p-1.5 text-coal-400 hover:bg-coal-100" onClick={closeModal}>
                  <X className="h-4 w-4" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">Parent Category *</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 focus:border-primary focus:outline-none"
                  >
                    <option value="">Select category</option>
                    {categoriesData?.map(c => (
                      <option key={c._id} value={c._id}>{c.name_en}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">English Name *</label>
                  <AppInput
                    placeholder="e.g. Algebra"
                    value={form.subCategory}
                    onChange={e => setForm(p => ({ ...p, subCategory: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">Arabic Name</label>
                  <AppInput
                    placeholder="مثال: جبر"
                    dir="rtl"
                    value={form.subCategory_ar}
                    onChange={e => setForm(p => ({ ...p, subCategory_ar: e.target.value }))}
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <AppButton type="submit" loading={isPending} className="flex-1">
                    <Save className="h-4 w-4" />
                    {editTarget ? 'Update' : 'Create'}
                  </AppButton>
                  <AppButton type="button" theme="default" onClick={closeModal} className="flex-1">Cancel</AppButton>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <>
          <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-xl border border-coal-200 bg-white shadow-dropdown p-6 text-center">
              <Trash2 className="mx-auto mb-3 h-10 w-10 text-danger" />
              <h3 className="text-base font-semibold text-coal-900 mb-2">Delete Subcategory</h3>
              <p className="text-sm text-coal-500 mb-5">
                Are you sure you want to delete <strong>"{deleteTarget.subCategory}"</strong>?
              </p>
              <div className="flex gap-3">
                <AppButton
                  theme="error"
                  className="flex-1"
                  loading={deleteSub.isPending}
                  onClick={() => deleteSub.mutate(deleteTarget._id)}
                >
                  Delete
                </AppButton>
                <AppButton theme="default" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</AppButton>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
